import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin } from 'lucide-react';

const Footer = () => {
  // Helper to generate URL-friendly path from location string
  const getPath = (locationText) => {
    let path = locationText.toLowerCase().replace(/\s+/g, '-');
    path = path.replace(/[^a-z0-9-]/g, '');
    return `/${path}`;
  };

  // Helper to generate Varanasi path for a loan type
  const getVaranasiPath = (loanType) => {
    const loanTypePath = loanType.toLowerCase().replace(/\s+/g, '-');
    return `/${loanTypePath}-in-varanasi`;
  };

  // Cities that should have their own dedicated pages (not redirected to Varanasi)
  const keepLocations = ['Azamgarh', 'Ballia', 'Chandauli', 'Ghazipur', 'Varanasi'];

  // Check if a location string should be kept (i.e., not redirected to Varanasi)
  const shouldKeep = (locationText) => {
    const city = locationText.split(' in ')[1];
    return keepLocations.includes(city);
  };

  // Extract loan type from location string
  const getLoanType = (locationText) => {
    return locationText.split(' in ')[0];
  };

  // Location arrays (now includes all five cities)
  const homeLoanLocations = [
    'Home Loan in Azamgarh', 'Home Loan in Ballia', 'Home Loan in Chandauli',
    'Home Loan in Ghazipur', 'Home Loan in Varanasi'
  ];

  const personalLoanLocations = [
    'Personal Loan in Azamgarh', 'Personal Loan in Ballia', 'Personal Loan in Chandauli',
    'Personal Loan in Ghazipur', 'Personal Loan in Varanasi'
  ];

  const businessLoanLocations = [
    'Business Loan in Azamgarh', 'Business Loan in Ballia', 'Business Loan in Chandauli',
    'Business Loan in Ghazipur', 'Business Loan in Varanasi'
  ];

  const newCarLoanLocations = [
    'New car Loan in Azamgarh', 'New car Loan in Ballia', 'New car Loan in Chandauli',
    'New car Loan in Ghazipur', 'New car Loan in Varanasi'
  ];

  const usedCarLoanLocations = [
    'Used car Loan in Azamgarh', 'Used car Loan in Ballia', 'Used car Loan in Chandauli',
    'Used car Loan in Ghazipur', 'Used car Loan in Varanasi'
  ];

  const usefulLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about-us' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Privacy Policy', path: '/privacy-policy' },
    { name: 'Terms And Condition', path: '/terms-and-conditions' },
  ];

  const ourServices = [
    { name: 'Home Loan', path: '/home-loan' },
    { name: 'Car Loan', path: '/carloan-Balance' },
    { name: 'Personal Loan', path: '/personal' },
    { name: 'Business Loan', path: '/business' },
    { name: 'Loan Against Property', path: '/loan-againstcar' },
  ];

  return (
    <footer className="bg-gradient-to-b from-green-800 to-green-900 text-gray-300">
      <div className="container mx-auto px-4 py-8">
        {/* Top section (logo, address, links, connect) – unchanged */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/logo1.png" alt="CashPro Logo" className="h-14 w-auto" />
              <span className="text-xl font-bold text-yellow-400">CashPro</span>
            </div>
            <div className="flex items-start gap-2">
              <MapPin size={16} className="text-yellow-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs leading-relaxed">
                2nd Floor Bhawani Complex Sigra Rathyatra<br />
                road Above SBI Bank Varanasi<br />
                Uttar Pradesh 221010
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-yellow-400 mb-2 border-l-2 border-yellow-400 pl-2">
              Useful Links
            </h3>
            <ul className="space-y-1">
              {usefulLinks.map((link) => (
                <li key={link.name}>
                  <Link to={link.path} className="text-xs hover:text-yellow-400 transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-yellow-400 mb-2 border-l-2 border-yellow-400 pl-2">
              Our Services
            </h3>
            <ul className="space-y-1">
              {ourServices.map((service) => (
                <li key={service.name}>
                  <Link to={service.path} className="text-xs hover:text-yellow-400 transition-colors">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-yellow-400 mb-2 border-l-2 border-yellow-400 pl-2">
              Connect With Us
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Phone size={14} className="text-yellow-400" />
                <a href="tel:8887141052" className="text-xs hover:text-yellow-400 transition-colors">
                  8887141052
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail size={14} className="text-yellow-400" />
                <a href="mailto:Info@cashpro.co.in" className="text-xs hover:text-yellow-400 transition-colors">
                  Info@cashpro.co.in
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Location Links Section */}
        <div className="border-t border-green-700 pt-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
            {/* Home Loan Locations */}
            <div>
              <h4 className="text-xs font-semibold text-yellow-400 mb-2">Home Loan Locations</h4>
              <ul className="space-y-1">
                {homeLoanLocations.map((item) => {
                  const path = shouldKeep(item) ? getPath(item) : getVaranasiPath(getLoanType(item));
                  return (
                    <li key={item}>
                      <Link to={path} className="text-[11px] hover:text-yellow-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Personal Loan Locations */}
            <div>
              <h4 className="text-xs font-semibold text-yellow-400 mb-2">Personal Loan Locations</h4>
              <ul className="space-y-1">
                {personalLoanLocations.map((item) => {
                  const path = shouldKeep(item) ? getPath(item) : getVaranasiPath(getLoanType(item));
                  return (
                    <li key={item}>
                      <Link to={path} className="text-[11px] hover:text-yellow-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Business Loan Locations */}
            <div>
              <h4 className="text-xs font-semibold text-yellow-400 mb-2">Business Loan Locations</h4>
              <ul className="space-y-1">
                {businessLoanLocations.map((item) => {
                  const path = shouldKeep(item) ? getPath(item) : getVaranasiPath(getLoanType(item));
                  return (
                    <li key={item}>
                      <Link to={path} className="text-[11px] hover:text-yellow-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* New Car Loan Locations */}
            <div>
              <h4 className="text-xs font-semibold text-yellow-400 mb-2">New Car Loan Locations</h4>
              <ul className="space-y-1">
                {newCarLoanLocations.map((item) => {
                  const path = shouldKeep(item) ? getPath(item) : getVaranasiPath(getLoanType(item));
                  return (
                    <li key={item}>
                      <Link to={path} className="text-[11px] hover:text-yellow-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Used Car Loan Locations */}
            <div>
              <h4 className="text-xs font-semibold text-yellow-400 mb-2">Used Car Loan Locations</h4>
              <ul className="space-y-1">
                {usedCarLoanLocations.map((item) => {
                  const path = shouldKeep(item) ? getPath(item) : getVaranasiPath(getLoanType(item));
                  return (
                    <li key={item}>
                      <Link to={path} className="text-[11px] hover:text-yellow-400 transition-colors">
                        {item}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-green-700 mt-6 pt-4 text-center text-[11px] text-gray-400">
          <p>© 2026 CashPro. All Rights Reserved.</p>
          <p className="mt-1">
            Designed by <span className="text-yellow-400">JDS Technology</span>
          </p>
          <p className="mt-1">
            CashPro Financial  © 2024. All Rights Reserved. | Powered by CashPro Financial
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;